import { cn } from '@/lib/cn';
import { Skeleton } from '../skeleton';
import { useSkeletonClock } from '../skeleton-clock';

/**
 * Placeholders for the sidebar while the account (`/auth/me`) or the org's
 * collections load. Each piece has the geometry of the real one (`SidebarOrg`,
 * `SidebarNavItem`, `SidebarSection`, `SidebarUser`), so the real rows replace
 * them without anything moving. They are plain `Skeleton`s: the ink surface
 * of the sidebar gives them their own base and highlight (kit-next.css), so
 * they read in light and dark.
 */

const LABEL_WIDTHS = ['w-24', 'w-28', 'w-20', 'w-32', 'w-24'] as const;

/** The org block (logo, name, plan) while the organizations load. */
export function SidebarOrgSkeleton({ className }: { className?: string }) {
  return (
    <div
      data-slot="sidebar-org"
      aria-hidden="true"
      className={cn('mb-3.5 flex h-[59px] w-full shrink-0 items-center gap-2.5 rounded-card border border-line p-2', className)}
    >
      <Skeleton className="size-[34px] shrink-0" />
      <span className="min-w-0 flex-1">
        <span className="flex h-[18px] items-center">
          <Skeleton className="h-3.5 w-28" />
        </span>
        <span className="mt-0.5 flex h-[19px] items-center">
          <Skeleton className="h-3.5 w-12" />
        </span>
      </span>
    </div>
  );
}

/** One nav row: an icon (or a collection dot) and a label, 36px like `SidebarNavItem`. */
export function SidebarNavRowSkeleton({ dot = false, width = 'w-24' }: { dot?: boolean; width?: string }) {
  return (
    <div aria-hidden="true" className="flex h-9 w-full shrink-0 items-center gap-2.5 px-2.5">
      {dot ? <Skeleton shape="pill" className="mx-1 size-2 shrink-0" /> : <Skeleton className="size-4 shrink-0" />}
      <Skeleton className={cn('h-3.5', width)} />
    </div>
  );
}

/** Label of a sidebar section ("Colecciones"), as `SidebarSection` lays it out. */
export function SidebarSectionLabelSkeleton() {
  return (
    <div aria-hidden="true" className="mt-3.5 flex flex-col px-2.5 pb-1.5 pt-1">
      <span className="flex h-[15px] items-center">
        <Skeleton className="h-2.5 w-20" />
      </span>
    </div>
  );
}

/**
 * Collection rows inside a loaded sidebar (its own announcement, because the
 * rest of the sidebar is not loading).
 */
export function SidebarRowsSkeleton({ rows = 3, label = 'Cargando colecciones' }: { rows?: number; label?: string }) {
  const ref = useSkeletonClock<HTMLDivElement>();
  return (
    <div ref={ref} data-slot="sidebar-skeleton" role="status" aria-label={label} className="flex flex-col gap-0.5">
      {Array.from({ length: rows }).map((_, i) => (
        <SidebarNavRowSkeleton key={i} dot width={LABEL_WIDTHS[i % LABEL_WIDTHS.length]} />
      ))}
    </div>
  );
}

/** The middle of the sidebar: Tours, the collections, then the org's sections. */
export function SidebarNavSkeleton({ className }: { className?: string }) {
  return (
    <div aria-hidden="true" className={cn('flex min-h-0 flex-1 flex-col gap-0.5', className)}>
      <SidebarNavRowSkeleton width="w-14" />
      <SidebarSectionLabelSkeleton />
      {Array.from({ length: 3 }).map((_, i) => (
        <SidebarNavRowSkeleton key={i} dot width={LABEL_WIDTHS[i]} />
      ))}
      <div role="separator" className="mx-1.5 my-2.5 h-px shrink-0 bg-line" />
      {Array.from({ length: 5 }).map((_, i) => (
        <SidebarNavRowSkeleton key={i} width={LABEL_WIDTHS[(i + 1) % LABEL_WIDTHS.length]} />
      ))}
    </div>
  );
}

/** The signed-in user at the bottom (avatar, name, "Mi cuenta"). */
export function SidebarUserSkeleton({ className }: { className?: string }) {
  return (
    <div
      data-slot="sidebar-user"
      aria-hidden="true"
      className={cn('mt-auto flex w-full shrink-0 items-center gap-2.5 rounded-card border border-line p-2', className)}
    >
      <Skeleton shape="pill" className="size-8 shrink-0" />
      <span className="min-w-0 flex-1">
        <span className="flex h-[18px] items-center">
          <Skeleton className="h-3.5 w-24" />
        </span>
        <span className="flex h-4 items-center">
          <Skeleton className="h-3 w-16" />
        </span>
      </span>
    </div>
  );
}

/**
 * The whole sidebar body while the account loads: org block, nav and user.
 * One announcement for the group; render it where the real pieces go (inside
 * `Sidebar` or `MobileDrawer`, below the brand).
 */
export function SidebarSkeleton({ className }: { className?: string }) {
  const ref = useSkeletonClock<HTMLDivElement>();
  return (
    <div
      ref={ref}
      data-slot="sidebar-skeleton"
      role="status"
      aria-label="Cargando el menú"
      className={cn('flex min-h-0 flex-1 flex-col', className)}
    >
      <SidebarOrgSkeleton />
      <SidebarNavSkeleton />
      <SidebarUserSkeleton />
    </div>
  );
}
